import { switchSubsidiary } from '~/features/auth/services/auth.service';
import { useAuthStore } from '~/store/auth.store';

interface SwitchTenantOptions {
  /**
   * Same-origin path (incl. search) to land on after the post-switch reload.
   * Lets a deep link — e.g. a shared experiment URL — survive the subsidiary
   * switch instead of always dropping the user on `/`. Defaults to `/`.
   */
  redirectTo?: string;
}

interface UseTenantSwitchResult {
  /**
   * Switch to a different tenant, optionally landing on a deep link afterwards.
   * Resolves `true` once the backend switch succeeds and the reload is triggered,
   * `false` if the switch failed — lets callers recover instead of hanging.
   */
  switchTenant: (tenantId: string, options?: SwitchTenantOptions) => Promise<boolean>;
  /** Whether a tenant switch is currently in progress. */
  isSwitching: boolean;
  /** Whether the current user is allowed to switch tenants (authenticated and not impersonating). */
  canSwitch: boolean;
}

/**
 * Reduce `redirectTo` to a safe same-origin path. Guards against open-redirect
 * (`//evil.com`, `/\evil.com`, absolute or scheme-relative URLs): requires a
 * single-slash-prefixed path, rejects backslashes (some browsers normalise `/\`
 * → `//` on navigation), then resolves against the current origin and confirms
 * the result stays same-origin. Anything else falls back to `/`.
 *
 * Also strips `?sub=`. Callers build `redirectTo` from the URL they are on, so it
 * carries the subsidiary being switched AWAY from; landing on it made
 * `reconcileSubsidiary` read that `sub` as a request for the old subsidiary and
 * switch straight back, silently undoing the switch. Stripping it here rather
 * than at each call site means no present or future caller can reintroduce the
 * loop. The guard back-fills the correct `sub` once the new token is in force.
 */
function safeRedirectTarget(redirectTo: string | undefined): string {
  if (!redirectTo || !redirectTo.startsWith('/') || redirectTo.startsWith('//')) return '/';
  if (redirectTo.includes('\\')) return '/';
  try {
    const url = new URL(redirectTo, window.location.origin);
    if (url.origin !== window.location.origin) return '/';
    url.searchParams.delete('sub');
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return '/';
  }
}

/**
 * Hook to switch the active tenant.
 * Available to any user with access to more than one tenant.
 *
 * Flow: POST /auth/switch-subsidiary → store new tokens → full page reload.
 * The reload re-runs the bootstrap sequence (main.tsx) which calls /auth/me
 * and /subsidiaries, fully reinitialising all stores, query caches, and UI state.
 */
export function useTenantSwitch(): UseTenantSwitchResult {
  const isSwitching = useAuthStore((state) => state.isSwitchingTenant);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const isImpersonating = useAuthStore((state) => state.isImpersonating);
  // Subsidiary switch is disabled during impersonation — the impersonation
  // session owns the token; a per-tab switch would shadow it (spec decision b).
  const canSwitch = isAuthenticated && !isImpersonating;

  async function switchTenant(tenantId: string, options?: SwitchTenantOptions): Promise<boolean> {
    const { setIsSwitchingTenant } = useAuthStore.getState();

    if (useAuthStore.getState().isImpersonating) return false;

    setIsSwitchingTenant(true);

    try {
      // Switch on the backend — returns new tokens scoped to the target
      // subsidiary. `switchSubsidiary` also persists the tokens via the
      // auth store, so the post-reload bootstrap picks them up.
      await switchSubsidiary(tenantId);

      // Full reload — reinitialises all stores, caches, permissions, and UI.
      // `redirectTo` (sanitised) preserves a deep link across the switch.
      window.location.replace(safeRedirectTarget(options?.redirectTo));
      return true;
    } catch {
      setIsSwitchingTenant(false);
      return false;
    }
  }

  return { switchTenant, isSwitching, canSwitch };
}
